/**
 * The sign-in screen: Google first (the staff's Workspace accounts), email and password as the
 * fallback. Only company addresses get in; the API enforces it, this screen just says so early.
 */
import { useState, type ReactNode } from 'react';
import { authConfigured, signInWithGoogle, signInWithPassword, takeRefusal } from '../lib/auth';
import { Mark } from './ui/mark';

const STAFF_DOMAIN = '@lifelongmerchantservices.com';

export function LoginScreen() {
  const [message, setMessage] = useState<string | null>(() => takeRefusal());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr.endsWith(STAFF_DOMAIN)) {
      setMessage('This desk is for Lifelong staff');
      return;
    }
    setBusy(true);
    void signInWithPassword(addr, password).then(({ error }) => {
      setBusy(false);
      if (error) setMessage(error);
    });
  };

  return (
    <div className="flex h-full items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <Mark className="size-9" />
          <div>
            <h1 className="text-title font-medium">Support Center</h1>
            <p className="text-hint text-ink-3">Lifelong Merchant Services</p>
          </div>
        </div>
        {!authConfigured && <Note>Sign-in is not configured on this build.</Note>}
        <button
          type="button"
          disabled={!authConfigured}
          onClick={() => {
            void signInWithGoogle().then(({ error }) => {
              if (error) setMessage(error);
            });
          }}
          className="h-10 w-full rounded-control bg-ink text-body font-medium text-canvas hover:opacity-90 disabled:opacity-50"
        >
          Continue with Google
        </button>
        <div className="my-5 flex items-center gap-3 text-hint text-ink-3">
          <span className="h-px flex-1 bg-line" />
          or your portal password, after a first Google sign-in
          <span className="h-px flex-1 bg-line" />
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="text-body text-ink-2" htmlFor="login-email">
            Email
          </label>
          <input id="login-email" type="email" autoComplete="username" className="-mt-2 h-10 rounded-control border border-line bg-surface-2 px-3 text-body focus:border-info focus-visible:outline-none" value={email} onChange={(e) => { setEmail(e.target.value); }} />
          <label className="text-body text-ink-2" htmlFor="login-password">
            Password
          </label>
          <input id="login-password" type="password" autoComplete="current-password" className="-mt-2 h-10 rounded-control border border-line bg-surface-2 px-3 text-body focus:border-info focus-visible:outline-none" value={password} onChange={(e) => { setPassword(e.target.value); }} />
          <button type="submit" disabled={busy || !authConfigured} className="h-9 rounded-control border border-line bg-surface-2 text-body font-medium text-ink hover:bg-surface-3 disabled:opacity-50">
            Sign in
          </button>
        </form>
        {message && (
          <p role="alert" className="mt-3 text-body text-alert">
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-body text-attention">{children}</p>;
}
